import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { Database } from '../lib/supabase'

type Product = Database['public']['Tables']['products']['Row']
type ProductOption = Database['public']['Tables']['product_options']['Row']
type ProductInsert = Database['public']['Tables']['products']['Insert']
type ProductUpdate = Database['public']['Tables']['products']['Update']

const PRODUCTS_QUERY_KEY = ['products'] as const
const PRODUCT_OPTIONS_QUERY_KEY = ['product-options'] as const

// Query functions
const fetchProducts = async (): Promise<Product[]> => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

const fetchProductOptions = async (productId?: string): Promise<ProductOption[]> => {
  let query = supabase
    .from('product_options')
    .select('*')
    .order('option_type', { ascending: true })

  if (productId) {
    query = query.eq('product_id', productId)
  }

  const { data, error } = await query

  if (error) throw error
  return data || []
}

const createProduct = async (product: ProductInsert): Promise<Product> => {
  const { data, error } = await supabase
    .from('products')
    .insert(product)
    .select()
    .single()

  if (error) throw error
  return data
}

const updateProduct = async ({ id, updates }: { id: string; updates: ProductUpdate }): Promise<Product> => {
  const { data, error } = await supabase
    .from('products')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

const deleteProduct = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Hooks
export const useProducts = () => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: PRODUCTS_QUERY_KEY,
    queryFn: fetchProducts,
  })

  const createMutation = useMutation({
    mutationFn: createProduct,
    onSuccess: (newProduct) => {
      queryClient.setQueryData(PRODUCTS_QUERY_KEY, (old: Product[] = []) => [newProduct, ...old])
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateProduct,
    onSuccess: (updatedProduct) => {
      queryClient.setQueryData(PRODUCTS_QUERY_KEY, (old: Product[] = []) =>
        old.map(product => product.id === updatedProduct.id ? updatedProduct : product)
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData(PRODUCTS_QUERY_KEY, (old: Product[] = []) =>
        old.filter(product => product.id !== deletedId)
      )
    },
  })

  return {
    products: query.data || [],
    loading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
    createProduct: createMutation.mutateAsync,
    updateProduct: (id: string, updates: ProductUpdate) => updateMutation.mutateAsync({ id, updates }),
    deleteProduct: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

export const useProductOptions = (productId?: string) => {
  const query = useQuery({
    queryKey: [...PRODUCT_OPTIONS_QUERY_KEY, productId],
    queryFn: () => fetchProductOptions(productId),
  })

  return {
    options: query.data || [],
    loading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
  }
}

// Helper function to group options by type
export const groupOptionsByType = (options: ProductOption[]) => {
  return options.reduce((acc, option) => {
    if (!acc[option.option_type]) {
      acc[option.option_type] = []
    }
    acc[option.option_type].push(option)
    return acc
  }, {} as Record<string, ProductOption[]>)
}
